import React from "react";
import "./Answer.css";
import "./AnswerTenPointSlider.css";
import "normalize.css";
import TenPointLikertSlider, {
  resolveTenPointThreeTierGif,
} from "./TenPointLikertSlider";
import stronglyAgree from "../../assets/strongly_agree.gif";
import agree from "../../assets/agree.gif";
import neutral from "../../assets/neutral.gif";
import sad from "../../assets/sad.gif";
import stronglySad from "../../assets/strongly_sad.gif";

const mapValuesOfEmojies = {
  1: stronglySad,
  2: sad,
  3: neutral,
  4: agree,
  5: stronglyAgree,
};

function toIntAnswerCode(answerCode) {
  const n = Number.parseInt(String(answerCode ?? "").trim(), 10);
  return Number.isNaN(n) ? null : n;
}

function fallbackFiveEmojiForTen(value) {
  const mapped = Math.round(((value - 1) / 9) * 4) + 1;
  return Math.min(5, Math.max(1, mapped));
}

function getLikertRatingTier(value, maxScale) {
  if (value === null) return "mid";
  if (maxScale >= 10) {
    if (value <= 6) return "low";
    if (value <= 8) return "mid";
    return "high";
  }
  if (value <= 2) return "low";
  if (value === 3) return "mid";
  return "high";
}

/** Likert image: optional 10-point three-tier; else 5-point map + legacy 1–10→5 spread. */
function resolveLTypeEmojiImgSrc(answerCode, useTenPointThreeTier = false) {
  const c = toIntAnswerCode(answerCode);
  if (c === null) return mapValuesOfEmojies[3];
  if (useTenPointThreeTier) return resolveTenPointThreeTierGif(c);
  return (
    mapValuesOfEmojies[c] ||
    (c >= 1 && c <= 10 ? mapValuesOfEmojies[fallbackFiveEmojiForTen(c)] : undefined) ||
    mapValuesOfEmojies[3]
  );
}

export default function Answer(props) {
  const {
    answer,
    onAnswerClick,
    qid,
    gid,
    type,
    isComment,
    isStart,
    customLTypeQuestion,
    initialLikertAnswerCode,
    onTenPointSliderCancel,
    isSubmit,
    /** When `answer` is a single givenAnswer object, pass question.answer.length so 10-point tier GIFs apply. */
    likertOptionCount,
  } = props;
  const isSubmitQuestion = Boolean(isSubmit);
  const shouldShowEmojiForLType =
    type === "L" && Boolean(customLTypeQuestion) && !isSubmitQuestion;
  const shouldRenderButtonList =
    isSubmitQuestion ||
    isStart ||
    (type === "L" ? !customLTypeQuestion : customLTypeQuestion);
  const scaleCount =
    (Array.isArray(customLTypeQuestion?.answer) &&
      customLTypeQuestion.answer.length) ||
    (Array.isArray(answer) && answer.length) ||
    (typeof likertOptionCount === "number" && likertOptionCount >= 2
      ? likertOptionCount
      : 0) ||
    5;
  const useTenPointLikertCard =
    !isSubmitQuestion &&
    type === "L" &&
    scaleCount === 10 &&
    Array.isArray(answer) &&
    Boolean(onAnswerClick);
  const useTenPointThreeTierEmoji = scaleCount === 10;
  const showTenPointSubmittedRating =
    shouldShowEmojiForLType && scaleCount === 10;

  return (
    <>
      {Array.isArray(answer) ? (
        shouldRenderButtonList ? (
          <>
            <div className="startSurveyContainer">
              {answer.map((ans, index) => {
                return (
                  <a
                    key={index}
                    className="startBtn mobile:text-xs mobile:py-2 mobile:px-4 text-base px-4 py-3"
                    onClick={() => {
                      onAnswerClick(ans, qid, gid, type);
                    }}
                  >
                    {" "}
                    {ans.answer}
                  </a>
                );
              })}
            </div>
          </>
        ) : useTenPointLikertCard ? (
          <div
            className={
              type === "L" ? "mine replyMessagesEmojies" : "mine replyMessages"
            }
          >
            <div
              className={
                type === "L"
                  ? "replyMessagesEmojies replyMessagesEmojies--ten-wide"
                  : "replyMessages"
              }
            >
              <TenPointLikertSlider
                answer={answer}
                qid={qid}
                gid={gid}
                type={type}
                onAnswerClick={onAnswerClick}
                initialAnswerCode={initialLikertAnswerCode}
                onCancel={onTenPointSliderCancel}
              />
            </div>
          </div>
        ) : (
          <div
            className={
              type === "L" ? "mine replyMessagesEmojies" : "mine replyMessages"
            }
          >
            <div
              className={
                type === "L" ? "replyMessagesEmojies" : "replyMessages"
              }
            >
              {answer
                .slice()
                .reverse()
                .map((ans) => {
                  const code = toIntAnswerCode(ans?.answer_code);
                  return (
                    <a
                      key={ans.aid}
                      onClick={() => {
                        onAnswerClick(ans, qid, gid, type);
                      }}
                      className={type === "L" ? "btnEmoji" : "btn"}
                    >
                      {type === "L" ? (
                        <span className="emoji-tooltip-wrapper">
                          <img
                            className="emoji-options w-10 mobile:w-8"
                            src={resolveLTypeEmojiImgSrc(
                              ans?.answer_code,
                              useTenPointThreeTierEmoji
                            )}
                            alt={ans?.answer || "emoji option"}
                          />
                          <span className="emoji-tooltip-text">{ans?.answer}</span>
                        </span>
                      ) : (
                        ans?.answer
                      )}
                    </a>
                  );
                })}
            </div>
          </div>
        )
      ) : (
        <div
          className={
            shouldShowEmojiForLType
              ? "mine replyMessagesEmojies"
              : "mine replyMessages"
          }
        >
          <div
            className={
              showTenPointSubmittedRating
                ? "likert-answer-card-wrap"
                : shouldShowEmojiForLType
                  ? "mine replyMessagesEmojies last"
                  : "mine replyMessagesAnsweres last text-base mobile:text-xs font-medium py-4 px-6 mobile:py-3.5 mobile:px-4"
            }
          >
            {showTenPointSubmittedRating ? (
              (() => {
                const ratingValue = toIntAnswerCode(answer?.answer_code);
                const tier = getLikertRatingTier(ratingValue, 10);
                return (
                  <div
                    className="likert-answer-card"
                    aria-label={
                      ratingValue !== null
                        ? `Rating ${ratingValue} out of 10`
                        : "Selected rating"
                    }
                  >
                    <img
                      className="likert-answer-card__emoji"
                      src={resolveLTypeEmojiImgSrc(
                        answer?.answer_code,
                        true
                      )}
                      alt={answer?.answer || "selected emoji"}
                    />
                    {/* {ratingValue !== null ? (
                      <div
                        className={`likert-answer-card__rating-pill likert-answer-card__rating-pill--${tier}`}
                      >
                        <span className="likert-answer-card__rating-num">
                          {ratingValue}
                        </span>
                        <span className="likert-answer-card__rating-denom">
                          /10
                        </span>
                      </div>
                    ) : null} */}
                  </div>
                );
              })()
            ) : shouldShowEmojiForLType ? (
              <img
                className="w-10 mobile:w-8"
                src={resolveLTypeEmojiImgSrc(answer?.answer_code, false)}
                alt={answer?.answer || "selected emoji"}
              />
            ) : isComment ? (
              answer?.comment
            ) : type === "L" ? (
              answer?.answer
            ) : (
              answer?.answer_code?.answer
            )}
          </div>
        </div>
      )}
    </>
  );
}
